import { isArray } from '../type';

/**
 * Create a empty array.
 */
export const emptyArray = <T>(): T[] => [];

/**
 * Generate a array for specified length.
 * @param length The length of the array.
 * @param createItem The callback for generating each item via index.
 */
export const createArray = <T>(
  length: number,
  createItem: (index: number) => T,
): T[] => {
  const array = Array(length);
  for (let index = 0; index < length; index++) {
    array[index] = createItem(index);
  }
  return array;
};

/**
 * Get the first item of the array.
 */
export const firstItem = <T>(array: ArrayLike<T>): T | undefined => array[0];

/**
 * Get the last item of the array.
 */
export const lastItem = <T>(array: ArrayLike<T>): T | undefined =>
  array[array.length - 1];

/**
 * Determine if two array are equal via determining whether each elements is equal
 * one by one through the strict equal `===`.
 * If there are non-primitive elements in the array, use `createArrayEqual` instead.
 */
export const arrayEqual = <T>(arrayA: T[], arrayB: T[]): boolean => {
  if (arrayA === arrayB) {
    return true;
  }
  if (arrayA.length !== arrayB.length) {
    return false;
  }
  const { length } = arrayA;
  for (let i = 0; i < length; i++) {
    if (arrayA[i] !== arrayB[i]) {
      return false;
    }
  }
  return true;
};

/**
 * Create a check function to determine if two array ore equal in all elements.
 * @param equal The callback to determine whether two elements are equal or not.
 */
export function createArrayEqual<T>(
  equal: (a: T, b: T) => boolean,
): (arrayA: T[], arrayB: T[]) => boolean {
  return function arrayEqual(arrayA: T[], arrayB: T[]): boolean {
    if (arrayA === arrayB) {
      return true;
    }
    if (arrayA.length !== arrayB.length) {
      return false;
    }
    const { length } = arrayA;
    for (let i = 0; i < length; i++) {
      if (!equal(arrayA[i], arrayB[i])) {
        return false;
      }
    }
    return true;
  };
}

/**
 * De-duplicates the elements of the array and returns a new array.
 * If there are non-primitive elements in the array, use `createDeDupe` instead.
 */
// export const deDupe: <T>(array: T[]) => T[] = (array) => [...new Set(array)];
export const deDupe = <T>(array: T[]): T[] => {
  const result: T[] = [];
  for (let i = 0; i < array.length; i++) {
    if (!result.includes(array[i])) {
      result.push(array[i]);
    }
  }
  return result;
};

/**
 * Create a de-duplication function. The function de-duplicate the elements of the array
 * via the passing in callback `equal` to determine if the elements are equal, and then
 * a new array is returned.
 * @param equal The callback to determine whether two elements are equal or not.
 */
export function createDeDupe<T>(
  equal: (a: T, b: T) => boolean,
): (array: T[]) => T[] {
  return function deDupe(array: T[]): T[] {
    const result: T[] = [];
    for (let i = 0; i < array.length; i++) {
      const element = array[i];
      if (!result.some((cur) => equal(cur, element))) {
        result.push(element);
      }
    }
    return result;
  };
}

/**
 * De-duplicates the sub arrays of the 2D array via `arrayEqual` and returns a new array.
 * If there are non-primitive elements in the sub arrays, use `createDeDupe` instead.
 */
export const deDupe2D: <T>(array: T[][]) => T[][] = createDeDupe(arrayEqual);

/**
 * Determine if an array is the start part of another.
 * @returns If `arrayA` is the start part of `arrayB`, returns true, otherwise returns false.
 */
export const arrayMatch = <T>(arrayA: T[], arrayB: T[]): boolean => {
  if (arrayA === arrayB) {
    return true;
  }
  if (arrayA.length > arrayB.length) {
    return false;
  }
  const { length } = arrayA;
  for (let i = 0; i < length; i++) {
    if (arrayA[i] !== arrayB[i]) {
      return false;
    }
  }
  return true;
};

export const isArray2D = (value: unknown): value is any[][] =>
  isArray(value) && (value as any[][]).every(isArray);

/**
 * If pass in an array, return the array directly.
 * If pass in a single item, wrap it within a new array.
 * Ensure that a value that may be an array or a single item is always an array.
 * @param value An array or a single item.
 */
export const ensureArray = <T>(value: T | T[]): T[] =>
  (isArray(value) && value) || [value];

/**
 * Ensure that the value is always a 2D array, if not, wrap it within a new array.
 * @param value The array that may be 1D or 2D.
 */
export const ensureArray2D = <T>(value: T[] | T[][]): T[][] =>
  (((isArray(value[0]) || value.length === 0) && value) || [value]) as T[][];

enum Condition {
  Smaller = -1,
  Unlimited,
  Bigger,
}
/**
 * Find the nearest value of a number in an unordered array
 * @param arr The source array to find
 * @param num Target number
 * @param condition Search conditions, bigger, smaller, or unlimited
 * @returns The number of closest target in the array
 */
export const closestItem = (
  arr: number[],
  num: number,
  condition: Condition = Condition.Unlimited,
): number => {
  let ret = num;
  let distance = Infinity;
  for (var i = 0; i < arr.length; i++) {
    var newDistance = Math.abs(arr[i] - num);
    if (newDistance < distance) {
      switch (condition) {
        case Condition.Smaller:
          if (arr[i] < num) {
            distance = newDistance;
            ret = arr[i];
          }
          break;
        case Condition.Bigger:
          if (arr[i] > num) {
            distance = newDistance;
            ret = arr[i];
          }
          break;
        default:
          distance = newDistance;
          ret = arr[i];
          break;
      }
    }
  }
  return ret;
};
